import React, { useCallback, useEffect, useState } from "react";
import { Scale, Radar } from "lucide-react";
import CrudModule from "@/components/CrudModule";
import { AlertEmails } from "@/components/AlertEmails";
import { LegalRowActions, LegalChanges, Iso6133Info, ComplianceBadge, MonitorBadge } from "@/components/LegalActions";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SPHERES = ["Federal", "Estadual", "Municipal", "Outros Requisitos"];

const Summary = ({ rows }) => (
  <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="legal-summary">
    {SPHERES.map((s) => {
      const list = rows.filter((r) => r.sphere === s);
      const ok = list.filter((r) => r.compliance === "Atende").length;
      const evaluated = list.filter((r) => !["Não avaliado", "Não aplicável", undefined].includes(r.compliance)).length;
      const pct = evaluated ? Math.round((ok / evaluated) * 100) : 0;
      const alerts = list.filter((r) => r.monitor_status === "Alteração para avaliar").length;
      return (
        <div key={s} className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`legal-summary-${s.split(" ")[0].toLowerCase()}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{s}</p>
          <p className="mt-1 text-2xl font-bold text-[#0B2545]">{list.length} <span className="text-xs font-medium text-slate-400">requisitos</span></p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
          <p className="mt-1 text-[11px] text-slate-500">{pct}% atende · {list.length - evaluated} sem avaliação{alerts ? <b className="text-rose-600"> · {alerts} alteração(ões)</b> : ""}</p>
        </div>
      );
    })}
  </div>
);

export default function LegalRequirements() {
  const { canManage } = useAuth();
  const [rows, setRows] = useState([]);
  const [sphere, setSphere] = useState("Federal");
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/legal-requirements").then(({ data }) => setRows(data)).catch(() => {}); }, [tick]);
  const bump = () => setTick((x) => x + 1);
  const rowFilter = useCallback((r) => r.sphere === sphere, [sphere]);
  const runAll = async () => {
    try { await api.post("/legal-monitor/run"); toast.success("Verificação de todas as fontes oficiais iniciada. Alterações chegam por e-mail e aparecem aqui."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="legal-page">
      <Summary rows={rows} />
      <LegalChanges refresh={tick} onReviewed={bump} />
      <div className="mb-4">
        <AlertEmails endpoint="/legal-settings" testid="legal-alert-emails" title="Quem recebe os avisos de Requisitos Legais"
          description="Lista própria (diferente dos avisos de ASO): alterações nas fontes oficiais com análise do que fazer, novos requisitos, modificações, não atendimentos e avaliações vencendo." />
      </div>
      <Tabs value={sphere} onValueChange={setSphere} className="mb-3">
        <TabsList className="flex h-auto flex-wrap" data-testid="legal-tabs">
          {SPHERES.map((s) => <TabsTrigger key={s} value={s} data-testid={`legal-tab-${s.split(" ")[0].toLowerCase()}`}>{s} ({rows.filter((r) => r.sphere === s).length})</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <CrudModule
        key={sphere}
        title="Requisitos Legais e Outros Requisitos"
        subtitle="Matriz MRL VALBRAC 001 · ISO 45001:2018 6.1.3 e 9.1.2 · monitoramento eletrônico das fontes oficiais"
        icon={Scale}
        endpoint="legal-requirements"
        exportKey="legal-requirements"
        testid="legal"
        rowFilter={rowFilter}
        onChanged={bump}
        rowExtra={(row, reload) => <LegalRowActions row={row} reload={() => { reload(); bump(); }} />}
        headerExtra={<><Iso6133Info />{canManage && <Button variant="outline" size="sm" onClick={runAll} data-testid="legal-run-monitor"><Radar className="mr-1.5 h-4 w-4 text-sky-600" /> Verificar fontes agora</Button>}</>}
        columns={[
          { key: "req_type", label: "Tipo" },
          { key: "number", label: "Número" },
          { key: "summary", label: "Ementa / Assunto", render: (r) => <span className="line-clamp-2 max-w-[340px] text-slate-700" title={r.summary}>{r.summary}</span> },
          { key: "agency", label: "Órgão" },
          { key: "compliance", label: "Atendimento", render: (r) => <ComplianceBadge value={r.compliance} /> },
          { key: "next_evaluation", label: "Próx. avaliação" },
          { key: "monitor_status", label: "Monitoramento", render: (r) => <MonitorBadge value={r.monitor_status} /> },
        ]}
        fields={[
          { name: "sphere", label: "Esfera", type: "select", options: SPHERES, default: sphere },
          { name: "req_type", label: "Tipo (Lei, Decreto, NR, Norma técnica, Requisito de cliente...)", type: "text", required: true },
          { name: "number", label: "Número / Ano", type: "text" },
          { name: "agency", label: "Órgão / Emissor", type: "text" },
          { name: "summary", label: "Ementa / Assunto", type: "textarea", required: true, full: true },
          { name: "requirement", label: "Requisito aplicável à Valbrac", type: "textarea", full: true },
          { name: "evidence", label: "Como atende / Evidência", type: "text" },
          { name: "periodicity", label: "Periodicidade de verificação", type: "text" },
          { name: "sgi_link", label: "Vínculo no SGI", type: "text" },
          { name: "vigia_control", label: "Controle no VIGIA", type: "text" },
          { name: "responsible", label: "Responsável", type: "text" },
          { name: "next_evaluation", label: "Próxima avaliação", type: "date" },
          { name: "official_url", label: "Link oficial para monitoramento (Planalto, DOU, ALESP, Prefeitura...)", type: "text", full: true },
        ]}
      />
    </div>
  );
}
